import { useRef, useLayoutEffect, useMemo, useEffect } from "react";
import { useWindowVirtualizer } from "@tanstack/react-virtual";
import { chunks } from "../helpers";
import { VideoCard } from "./VideoCard";
import { Row, Col, Grid } from "antd";
import { Video } from "../types";

export type BatchItemsListProps = {
  videos: Video[];
  showStatus: boolean;
  hasNextPage: boolean | undefined;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
};

export const BatchItemsList = ({
  videos,
  showStatus,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
}: BatchItemsListProps) => {
  const breakpoint = Grid.useBreakpoint();
  const chunkSize = breakpoint.xl ? 3 : 2;

  const parentRef = useRef<HTMLDivElement>(null);
  const parentOffsetRef = useRef(0);
  useLayoutEffect(() => {
    parentOffsetRef.current = parentRef.current?.offsetTop ?? 0;
  }, []);

  const videoChunks = useMemo(
    () => [...chunks(videos, chunkSize)],
    [videos, chunkSize]
  );

  // Only mount the rows currently in/near the viewport - rendering all loaded
  // videos at once is what freezes the tab once a batch has hundreds of them
  const rowVirtualizer = useWindowVirtualizer({
    count: videoChunks.length,
    estimateSize: () => (showStatus ? 220 : 650),
    overscan: 3,
    scrollMargin: parentOffsetRef.current,
  });

  // Fetch the next page once the user has actually scrolled near the last
  // loaded row, instead of eagerly prefetching pages nobody has scrolled to yet
  const virtualItems = rowVirtualizer.getVirtualItems();
  const lastVirtualItemIndex = virtualItems.at(-1)?.index ?? -1;

  useEffect(() => {
    if (
      hasNextPage &&
      !isFetchingNextPage &&
      lastVirtualItemIndex >= videoChunks.length - 1
    ) {
      fetchNextPage();
    }
  }, [
    lastVirtualItemIndex,
    videoChunks.length,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  ]);

  return (
    <div ref={parentRef}>
      <div
        style={{
          position: "relative",
          width: "100%",
          height: rowVirtualizer.getTotalSize(),
        }}
      >
        {rowVirtualizer.getVirtualItems().map((virtualRow) => {
          const chunk = videoChunks[virtualRow.index];
          return (
            <div
              key={virtualRow.key}
              data-index={virtualRow.index}
              ref={rowVirtualizer.measureElement}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                paddingBottom: 30,
                transform: `translateY(${
                  virtualRow.start - rowVirtualizer.options.scrollMargin
                }px)`,
              }}
            >
              <Row gutter={[30, 30]}>
                {chunk?.map((video) => (
                  <Col key={video.id} span={24} md={24 / chunkSize}>
                    <VideoCard video={video} />
                  </Col>
                ))}
              </Row>
            </div>
          );
        })}
      </div>
    </div>
  );
};
